import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { FaUserTie, FaCheckCircle, FaArrowLeft, FaMapMarkerAlt, FaEdit, FaEye } from 'react-icons/fa';
import InfoModal from '../components/InfoModal';

const JobBiddersPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBidders, setSelectedBidders] = useState([]); 
  const [savedSelectedBidders, setSavedSelectedBidders] = useState([]); 
  const [saving, setSaving] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });

  const showInfoModal = (title, message) => {
    setModalContent({ title, message });
    setIsInfoModalOpen(true);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const fetchJobAndBids = useCallback(async () => {
    setLoading(true);
    try {
      const jobResponse = await apiClient.get(`/jobs/${jobId}`);
      setJobTitle(jobResponse.data.title);

      const existingSelections = jobResponse.data.selected_bidders?.map(s => s.applicant_profile_id) || [];
      setSavedSelectedBidders(existingSelections);
      setSelectedBidders(existingSelections); 

      const bidsResponse = await apiClient.get(`/jobs/${jobId}/bids`, {
        params: { page: currentPage, limit }
      });
      setBids(bidsResponse.data.bids);
      setTotalPages(bidsResponse.data.pages);

    } catch (err) {
      setError('Failed to load data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [jobId, currentPage]);

  useEffect(() => {
    fetchJobAndBids();
  }, [fetchJobAndBids]);

  const handleCheckboxChange = (bidderId) => {
    setSelectedBidders(prev => {
      if (prev.includes(bidderId)) {
        return prev.filter(id => id !== bidderId);
      } else {
        return [...prev, bidderId];
      }
    });
  };

  const handleSaveSelection = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/jobs/${jobId}/select-bidders`, {
        selectedBidderIds: selectedBidders
      });
      setSavedSelectedBidders(selectedBidders);
      setIsEditing(false);
      showInfoModal('Success', 'Bidders selected successfully!');
    } catch (err) {
      showInfoModal('Error', err.response?.data?.message || 'Failed to save selection.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEditMode = () => {
    if (isEditing) {
      setSelectedBidders(savedSelectedBidders);
    }
    setIsEditing(!isEditing);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
  if (error) return <div className="text-center text-red-500 mt-10">{error}</div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <InfoModal 
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
      />
      <button onClick={() => navigate('/owner-dashboard', { state: { activeTab: 'published' } })} className="flex items-center text-gray-600 mb-6 hover:text-gray-800">
        <FaArrowLeft className="mr-2" /> Back to Published Jobs
      </button>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-8 border-b flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Select Bidders for: {jobTitle}</h1>
            <p className="text-gray-500 mt-2">Select the bidders you want to hire for this job.</p>
          </div>

          <button
            onClick={toggleEditMode}
            className={`flex items-center px-4 py-2 rounded-lg font-semibold transition-colors ${isEditing ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {isEditing ? 'Cancel Selection' : (
              <>
                <FaEdit className="mr-2" /> Select/Edit Bidders
              </>
            )}
          </button>
        </div>

        <div className="p-8">
          {bids.length > 0 ? (
            <div className="space-y-4">
              {bids.map((bid) => {
                const isSelected = savedSelectedBidders.includes(bid.bidder_profile_id);
                const isChecked = selectedBidders.includes(bid.bidder_profile_id);
                return (
                  <div key={bid.id} className={`flex flex-col md:flex-row items-center justify-between p-4 rounded-xl border transition-all ${isChecked && isEditing ? 'border-blue-500 bg-blue-50' : (isSelected && !isEditing ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-blue-300')}`}>
                    <div className="flex items-center space-x-4 w-full md:w-auto">
                      {isEditing && (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(bid.bidder_profile_id)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
                        />
                      )}

                      {!isEditing && isSelected && (
                        <div className="text-green-600" title="Selected">
                          <FaCheckCircle size={24} />
                        </div>
                      )}

                      <div className="flex items-center space-x-3">
                        {bid.bidder?.picture ? (
                          <img src={loadImage(bid.bidder.picture)} alt={bid.bidder.name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                            <FaUserTie />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800">
                            {bid.bidder?.name || bid.bidder?.User?.username || 'Unknown User'}
                          </p>
                          {bid.bidder?.location && (
                            <p className="text-xs text-gray-500 flex items-center mt-1">
                              <FaMapMarkerAlt className="mr-1" /> {bid.bidder.location}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 mt-4 md:mt-0 w-full md:w-auto justify-end">
                      <div className="text-center md:text-right">
                        <p className="text-sm text-gray-500">Bid Amount</p>
                        <p className="font-bold text-blue-600 text-lg">৳{bid.Bid_Amount.toFixed(2)}</p>
                      </div>

                      <button
                        onClick={() => navigate(`/applicant-details/${bid.bidder_profile_id}`)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center border border-blue-200 px-3 py-1 rounded-full hover:bg-blue-50 transition-colors"
                      >
                        <FaEye className="mr-1" /> Show Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-10">No bids received yet.</p>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center mt-8 space-x-2">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 rounded border disabled:opacity-50">Prev</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} onClick={() => handlePageChange(i + 1)} className={`px-3 py-1 rounded border ${currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-white'}`}>{i + 1}</button>
              ))}
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 rounded border disabled:opacity-50">Next</button>
            </div>
          )}

          {isEditing && (
            <div className="mt-8 flex justify-end sticky bottom-0 bg-white p-4 border-t shadow-inner">
              <button
                onClick={handleSaveSelection}
                disabled={saving}
                className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center shadow-lg"
              >
                <FaCheckCircle className="mr-2" />
                {saving ? 'Saving...' : 'Confirm Selection'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobBiddersPage;
